import { AxiosError } from 'axios';
import React, { useCallback, useRef, useState } from 'react';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Fade,
  LinearProgress,
  Slide,
  Typography,
} from '@mui/material';
// components
import { FilePreview } from './FilePreview';
import { MessageList } from './MessageList';
import { FileUploadArea } from './FileUploadArea';
import { StyledDialogTitle } from 'theme/styleComponent';
// constants
import { HTTP_STATUS, UPLOAD_FAILED_TEXT, UPLOAD_TEXT } from 'constants/index';
// interfaces
import { UploadModalProps } from 'interfaces';
import { Nullable } from 'interfaces';
// helpers
import { importIngredient, validateCSVContent } from 'lib/helper';

export const IngredientModal: React.FC<UploadModalProps> = ({
  open,
  messages = [],
  onClose,
  onDownloadSample,
  loading = false,
}) => {
  const [selectedFile, setSelectedFile] = useState<Nullable<File>>(null);
  const [dragActive, setDragActive] = useState<boolean>(false);
  const [error, setError] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetState = useCallback(() => {
    setSelectedFile(null);
    setError([]);
    setDragActive(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleFileSelect = useCallback(async (file: File) => {
    // Additional CSV content validation
    const csvValidationError = await validateCSVContent(file);
    if (csvValidationError) {
      setError(csvValidationError.map((error) => error.message));
      setSelectedFile(null);
      return;
    }

    setError([]);
    setSelectedFile(file);
  }, []);

  const handleUpload = useCallback(async () => {
    if (!selectedFile || loading) return;

    try {
      await importIngredient(selectedFile, true);
      onClose();
      resetState();
    } catch (error) {
      const { message, response } = error as AxiosError;
      if (response?.status === HTTP_STATUS.BAD_REQUEST) {
        const { data } = response;

        const { errors } = data as unknown as { errors: string[] };
        if (Array.isArray(errors)) {
          setError(errors);
        } else {
          setError([errors]);
        }
        return;
      }
      const errorMessage = error instanceof Error ? error.message : UPLOAD_FAILED_TEXT;
      setError([`Upload Error: ${errorMessage} ${message}`]);
    }
  }, [selectedFile, loading, resetState, onClose]);

  const handleRemoveFile = useCallback(() => {
    resetState();
  }, [resetState]);

  const handleClose = useCallback(() => {
    if (!loading) {
      resetState();
      onClose();
    }
  }, [loading, resetState, onClose]);

  const handleDragStateChange = useCallback((active: boolean) => {
    setDragActive(active);
  }, []);

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      onClose={handleClose}
      aria-labelledby="ingredient-modal-title"
      TransitionComponent={Slide}>
      <StyledDialogTitle id="ingredient-modal-title">
        <Typography variant="h6" component="h2">
          {UPLOAD_TEXT.UPLOAD_TITLE}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {UPLOAD_TEXT.UPLOAD_SUBTITLE}
        </Typography>
      </StyledDialogTitle>

      <DialogContent sx={{ mt: 3 }}>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <MessageList messages={messages} />

        {error.length > 0 && (
          <Fade in={!!error.length}>
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError([])}>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {error.map((msg, idx) => (
                  <li key={idx}>{msg}</li>
                ))}
              </ul>
            </Alert>
          </Fade>
        )}

        <FileUploadArea
          ref={fileInputRef}
          selectedFile={selectedFile}
          dragActive={dragActive}
          error={error.length > 0 ? error[0] : ''}
          loading={loading}
          onFileSelect={handleFileSelect}
          onDragStateChange={handleDragStateChange}
        />

        {selectedFile && <FilePreview file={selectedFile} onRemove={handleRemoveFile} loading={loading} />}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 3 }}>
        <Button
          color="primary"
          variant="outlined"
          onClick={onDownloadSample}
          disabled={loading}
          startIcon={<CloudUploadIcon />}>
          {UPLOAD_TEXT.DOWNLOAD_SAMPLE}
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpload}
            disabled={!selectedFile || loading}
            startIcon={loading ? null : <CloudUploadIcon />}
            sx={{ minWidth: 100 }}>
            {loading ? UPLOAD_TEXT.UPLOADING : UPLOAD_TEXT.UPLOAD_BUTTON}
          </Button>
          <Button variant="outlined" color="primary" onClick={handleClose} disabled={loading}>
            {UPLOAD_TEXT.CLOSE}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
